import { listActiveMemos } from '../storage/memosStore.js';
import { calculateStats } from '../domain/stats.js';
import { renderStatCards } from './components/stat-card.js';
import { renderForestGrid } from './components/forest-grid.js';
import { showForestModal } from './components/forest-modal.js';
import { showDayDetailSheet } from './components/day-detail-sheet.js';
import { api } from '../api/client.js';
import { getCachedSummary, setCachedSummary } from '../api/ai-cache.js';
import { showCelebration } from './components/toast.js';
import type { Memo } from '@memori-web/shared';

const TODAY_DATE = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};

export function renderForestView(root: HTMLElement): () => void {
  const today = TODAY_DATE();
  const state = {
    year: parseInt(today.split('-')[0]!, 10),
    month: parseInt(today.split('-')[1]!, 10),
    memos: [] as Memo[],
  };

  root.innerHTML = `
    <div class="forest-view">
      <div id="forest-stats"></div>
      <div id="forest-grid"></div>
      <p class="forest-view__hint">日付をタップして詳細を見る</p>
    </div>
  `;
  const statsEl = root.querySelector<HTMLDivElement>('#forest-stats')!;
  const gridEl = root.querySelector<HTMLDivElement>('#forest-grid')!;

  const openYearModal = async () => {
    await showForestModal(state.memos, today, {
      onSelectDay: (d) => {
        const [y, m] = d.split('-').map(Number);
        state.year = y!; state.month = m!;
        refresh();
        handleSelectDay(d);
      },
    });
  };

  const handleSelectDay = async (date: string) => {
    // 詳細 sheet を開く前に D1 から既存要約を取得して cache に load
    // (sheet 内で日付切替された時は同期的に getCachedSummary を引くだけなので、
    //  先回りで近隣日の要約も取りたいところ。今は initialDate のみ先読みし、
    //  日付切替時は cache miss を許容)
    if (!getCachedSummary(date)) {
      try {
        const fetched = await api.getSummary(date);
        if (fetched) setCachedSummary(date, fetched);
      } catch {
        // network 失敗は無視 (cache miss と同じ扱い)
      }
    }
    await showDayDetailSheet({
      memos: state.memos,
      initialDate: date,
      todayDate: today,
      getCachedSummary: (d) => getCachedSummary(d),
      onGenerateSummary: async (d) => {
        const result = await api.summarize(d);
        setCachedSummary(d, result);
        showCelebration('要約ができたよ ✨');
        return result;
      },
    });
    // 詳細シート閉じた後はメモが新たに増えてる可能性があるので refresh
    refresh();
  };

  const refresh = async () => {
    state.memos = await listActiveMemos();
    renderStatCards(statsEl, calculateStats(state.memos, today));
    renderForestGrid(gridEl, state.memos, state.year, state.month, '', today, {
      onSelectDay: handleSelectDay,
    });
    // 月ナビボタン + 1 年ボタン (forest-grid 内) のフック
    gridEl.querySelectorAll<HTMLButtonElement>('[data-action]').forEach(btn => {
      btn.addEventListener('click', () => {
        const action = btn.dataset.action;
        if (action === 'year') {
          openYearModal();
          return;
        }
        const dir = action === 'next' ? 1 : -1;
        let m = state.month + dir;
        let y = state.year;
        if (m > 12) { m = 1; y++; }
        if (m < 1) { m = 12; y--; }
        state.year = y; state.month = m;
        refresh();
      });
    });
  };

  refresh();
  return () => {};
}
